// The modal MOUNT POINT (design brief §2/§4): one Task detail/thread. Email is read inline in the owning
// conversation, so the shell has no second email-reader state or alternate renderer. This always-mounted
// component retains the bounded draft + exact uncertain-request correlation for the current page lifetime.
// Refusal notes, queued-run observers, and stream state remain inside the exact modal/controller instance.

import { useCallback, useRef, useState } from 'react';
import { useShell } from './ShellContext.tsx';
import { TaskDetailModal } from './TaskDetailModal.tsx';
import {
  canRetainTaskRequest,
  reduceTaskDraftRegistry,
  type TaskDraftRegistry,
} from './task-draft-registry.ts';
import type { TaskComposerRetentionEvent } from '../surfaces/task-request-identity.ts';

/** Render the Task modal when open. */
export function ModalMounts(): React.JSX.Element | null {
  const shell = useShell();
  const taskId = shell.taskModalId;
  const activeTaskId = useRef<string | null>(taskId);
  activeTaskId.current = taskId;
  const taskDraftsRef = useRef<TaskDraftRegistry>(new Map());
  const [taskDrafts, setTaskDrafts] = useState<TaskDraftRegistry>(taskDraftsRef.current);
  const retainTaskComposer = useCallback(
    (targetTaskId: string, event: TaskComposerRetentionEvent): boolean => {
      const current = taskDraftsRef.current;
      if (
        event.kind === 'request_started' &&
        !canRetainTaskRequest(current, targetTaskId, activeTaskId.current)
      ) {
        return false;
      }
      const next = reduceTaskDraftRegistry(current, targetTaskId, event, activeTaskId.current);
      taskDraftsRef.current = next;
      setTaskDrafts(next);
      return true;
    },
    [],
  );
  if (taskId === null) return null;
  return (
    /* Keyed by the Task: a second `#tasks/<id>` deep link retargets the OPEN modal, and its display
       state (the hide-chat layout, the entrance transition) belongs to the Task being left. */
    <TaskDetailModal
      key={taskId}
      taskId={taskId}
      retention={taskDrafts.get(taskId) ?? null}
      onRetentionEvent={(event) => retainTaskComposer(taskId, event)}
      onClose={shell.closeTaskModal}
    />
  );
}
